/**
 * Newsletter forms
 * - Validates the fields client-side (replaces the native browser validation
 *   popup with a custom message under the offending field).
 * - For Klaviyo-enabled forms, subscribes via the Klaviyo Client API and
 *   shows the success message under the form without hiding the fields.
 * - Lets non-Klaviyo forms submit normally once validation passes.
 *
 * Email is the only field the simple forms (footer, newsletter section, popup)
 * carry. Richer forms — the password landing page — opt into extra profile
 * fields by marking their inputs:
 *   data-klaviyo-first-name   -> profile first_name
 *   data-klaviyo-last-name    -> profile last_name
 *   data-klaviyo-phone        -> profile phone_number (E.164, see normalizePhone)
 * and opt the form into SMS consent with data-klaviyo-sms-consent.
 *
 * A question group — any element with data-klaviyo-property="<key>" wrapping
 * checkboxes — saves the ticked value to that profile property. One answer
 * only: ticking a box clears the others in its group. Nothing ticked sends
 * nothing, so an unanswered question never blanks an answer given elsewhere
 * (the Survey page writes the same property). Add data-klaviyo-required to
 * the group to block the signup until a box is ticked; the message comes from
 * its data-missing-message and shows in the group's own
 * [data-newsletter-validation] element.
 */
(function () {
  // Several sections include this file (footer, popup, newsletter), and each
  // <script> runs it again — which bound every listener twice and sent each
  // signup to Klaviyo twice. Only the first copy binds.
  if (window.__klaviyoSubscribeBound) return;
  window.__klaviyoSubscribeBound = true;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var E164_RE = /^\+[1-9]\d{7,14}$/;

  /*
    National-number lengths, by dial code, for the countries we can state
    exactly. A typo'd US number is otherwise perfectly valid E.164 and reaches
    Klaviyo as a real-looking number that belongs to nobody.

    Deliberately short: an entry that's wrong rejects real customers, which is
    worse than the permissive E.164 range everything else falls back to. The
    NANP (US, Canada, Caribbean) is a firm 10 digits — add others only where
    they're equally certain.
  */
  var NATIONAL_LENGTHS = {
    '1': [10, 10]
  };

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('.newsletter-form');
    if (!form) return;

    var emailInput = form.querySelector('input[type="email"]');
    if (!emailInput) return;

    clearValidationErrors(form);
    hideStatusMessages(form);

    var firstInvalid = validate(form);
    if (firstInvalid) {
      e.preventDefault();
      firstInvalid.focus();
      return;
    }

    if (form.hasAttribute('data-klaviyo-form')) {
      e.preventDefault();
      submitToKlaviyo(form, emailInput);
    }
  });

  // Clear a field's validation error as the user types/edits.
  document.addEventListener('input', function (e) {
    var input = e.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.closest('.newsletter-form')) return;
    clearValidationError(input);
  });

  // Question groups: checkboxes that behave like radios, but can be unticked.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox' || !input.checked) return;
    var group = input.closest('[data-klaviyo-property]');
    if (!group || !group.closest('.newsletter-form')) return;
    if (group.hasAttribute('data-klaviyo-required')) clearGroupError(group);
    var boxes = group.querySelectorAll('input[type="checkbox"]');
    for (var i = 0; i < boxes.length; i++) {
      if (boxes[i] !== input) boxes[i].checked = false;
    }
  });

  // Picking a different country re-reads the number, so drop any stale error.
  document.addEventListener('change', function (e) {
    var select = e.target;
    if (!select || !select.hasAttribute || !select.hasAttribute('data-klaviyo-phone-country')) return;
    var form = select.closest('.newsletter-form');
    if (!form) return;
    var phoneInput = form.querySelector('[data-klaviyo-phone]');
    if (phoneInput) clearValidationError(phoneInput);
  });

  /* --------------------------------------------------------------- validation */

  // Marks every invalid field and returns the first one in DOM order, or null.
  function validate(form) {
    var inputs = form.querySelectorAll('input');
    var firstInvalid = null;

    for (var i = 0; i < inputs.length; i++) {
      var message = validateInput(inputs[i]);
      if (!message) continue;
      showValidationError(inputs[i], message);
      if (!firstInvalid) firstInvalid = inputs[i];
    }

    // Question groups marked data-klaviyo-required need a ticked box. Focus
    // goes to the group's first box, after any field above it.
    var groups = form.querySelectorAll('[data-klaviyo-property][data-klaviyo-required]');
    for (var g = 0; g < groups.length; g++) {
      var firstBox = groups[g].querySelector('input[type="checkbox"]');
      if (!firstBox || groups[g].querySelector('input[type="checkbox"]:checked')) continue;
      showGroupError(groups[g], groups[g].getAttribute('data-missing-message') || 'Please choose an answer.');
      if (!firstInvalid) firstInvalid = firstBox;
    }

    return firstInvalid;
  }

  function validateInput(input) {
    if (input.type === 'hidden' || input.type === 'submit' || input.disabled) return '';
    if (input.type === 'checkbox' || input.type === 'radio') return '';

    var value = input.value.trim();

    if (!value) {
      if (!input.hasAttribute('required')) return '';
      return input.dataset.missingMessage || 'This field is required';
    }

    if (input.type === 'email' && !EMAIL_RE.test(value)) {
      return input.dataset.invalidMessage || 'Please enter a valid email address';
    }

    if (input.hasAttribute('data-klaviyo-phone')) {
      var e164 = toE164(input);
      if (!e164) return input.dataset.invalidMessage || 'Please enter a valid phone number';

      // Say how many digits are expected rather than just "invalid" — the
      // visitor can't otherwise tell what's wrong with their own number.
      var expected = nationalLengthMismatch(e164);
      if (expected) {
        var count = expected[0] === expected[1] ? expected[0] : expected[0] + '-' + expected[1];
        var template = input.dataset.lengthMessage || 'Please enter a [count]-digit phone number';
        return template.replace('[count]', count);
      }
    }

    return '';
  }

  /*
    Klaviyo rejects the whole subscription — the email along with it — when
    phone_number isn't E.164, so the number is normalised here and the field
    fails validation rather than silently taking the email signup down with it.
    A number typed without a country code gets data-default-country-code.
  */
  // The number as Klaviyo will receive it, or '' if it can't be trusted.
  function normalizePhone(input) {
    var e164 = toE164(input);
    if (!e164) return '';
    return nationalLengthMismatch(e164) ? '' : e164;
  }

  // Returns the expected [min, max] national length when the number doesn't
  // match it, or null when there's nothing to say.
  function nationalLengthMismatch(e164) {
    for (var code in NATIONAL_LENGTHS) {
      if (!Object.prototype.hasOwnProperty.call(NATIONAL_LENGTHS, code)) continue;
      if (e164.indexOf('+' + code) !== 0) continue;

      var national = e164.length - 1 - code.length;
      var range = NATIONAL_LENGTHS[code];
      return national < range[0] || national > range[1] ? range : null;
    }
    return null;
  }

  // Formatting only — the length rules live in nationalLengthMismatch.
  function toE164(input) {
    var raw = input.value.trim();
    if (!raw) return '';

    var digits = raw.replace(/[^\d+]/g, '');
    var hasPlus = digits.charAt(0) === '+';
    digits = (hasPlus ? '+' : '') + digits.replace(/\+/g, '');

    if (!hasPlus) {
      if (digits.indexOf('00') === 0) {
        /*
          "00" is the international access prefix across Europe and much of the
          world, so someone writing 00386... means +386... . Bolting the default
          country code onto that instead would send Klaviyo a real-looking but
          entirely wrong number.
        */
        digits = '+' + digits.slice(2);
      } else {
        var defaultCode = countryCode(input);
        if (!defaultCode) return '';
        // Drop the national trunk prefix ("0") before prefixing a country code.
        digits = '+' + defaultCode + digits.replace(/^0+/, '');
      }
    }

    return E164_RE.test(digits) ? digits : '';
  }

  /*
    The country the number should be read against: whatever the visitor picked
    in the country select, falling back to the field's own default for forms
    that don't offer one (a US-only store, say).
  */
  function countryCode(input) {
    var wrapper = input.closest('.field') || input.parentElement;
    var select = wrapper && wrapper.querySelector('[data-klaviyo-phone-country]');
    var value = select && select.value ? select.value : input.getAttribute('data-default-country-code');
    return (value || '').replace(/\D/g, '');
  }

  /* ------------------------------------------------------------------- submit */

  function submitToKlaviyo(form, emailInput) {
    var listId = form.getAttribute('data-klaviyo-list');
    var apiKey = form.getAttribute('data-klaviyo-api-key');
    if (!listId || !apiKey) return;

    var submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    var attributes = profileAttributes(form, emailInput);

    /*
      Two calls, on purpose. /client/subscriptions/ owns list membership and
      consent, but it's documented as a consent endpoint — the name and phone
      attributes it accepts aren't reliably written to the profile, which is
      why a signup would land in Klaviyo with the email alone.
      /client/profiles/ is the endpoint that does persist them.

      Both carry the same attributes so neither can blank out the other's, and
      only the subscription decides what the visitor is shown: losing the
      attribute write shouldn't cost someone their signup.
    */
    upsertProfile(apiKey, attributes);

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-02-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: form.getAttribute('data-klaviyo-source') || 'Website',
            profile: {
              data: {
                type: 'profile',
                attributes: attributes
              }
            }
          },
          relationships: {
            list: {
              data: { type: 'list', id: listId }
            }
          }
        }
      })
    })
      .then(function (res) {
        if (res.ok || res.status === 202) {
          resetFields(form);
          showMessage(form, 'success');
        } else {
          showMessage(form, 'error');
        }
      })
      .catch(function () {
        showMessage(form, 'error');
      })
      .finally(function () {
        if (submitBtn) submitBtn.disabled = false;
      });
  }

  function profileAttributes(form, emailInput) {
    var attributes = { email: emailInput.value.trim() };

    var firstName = fieldValue(form, '[data-klaviyo-first-name]');
    if (firstName) attributes.first_name = firstName;

    var lastName = fieldValue(form, '[data-klaviyo-last-name]');
    if (lastName) attributes.last_name = lastName;

    var phoneInput = form.querySelector('[data-klaviyo-phone]');
    var phone = phoneInput ? normalizePhone(phoneInput) : '';
    if (phone) {
      attributes.phone_number = phone;

      /*
        Klaviyo only accepts phone_number as an identifier for countries its
        SMS covers — Slovenia and Serbia, among others, aren't on that list — so
        the number is also kept as a plain profile property. That copy is always
        stored and exportable whatever Klaviyo does with the identifier, which
        is the difference between capturing a signup's phone and losing it.
      */
      attributes.properties = { signup_phone: phone };
      /*
        SMS consent is opt-in per form: Klaviyo rejects the request when the
        list isn't SMS-enabled. Naming subscriptions at all means naming email
        too, since it's no longer implied.
      */
      if (form.hasAttribute('data-klaviyo-sms-consent')) {
        attributes.subscriptions = {
          email: { marketing: { consent: 'SUBSCRIBED' } },
          sms: { marketing: { consent: 'SUBSCRIBED' } }
        };
      }
    }

    var answers = questionAnswers(form);
    for (var answerKey in answers) {
      if (!Object.prototype.hasOwnProperty.call(answers, answerKey)) continue;
      attributes.properties = attributes.properties || {};
      attributes.properties[answerKey] = answers[answerKey];
    }

    return attributes;
  }

  // { "<property>": "<ticked value>" } for each answered question group.
  function questionAnswers(form) {
    var answers = {};
    var groups = form.querySelectorAll('[data-klaviyo-property]');
    for (var i = 0; i < groups.length; i++) {
      var key = groups[i].getAttribute('data-klaviyo-property');
      var ticked = groups[i].querySelector('input[type="checkbox"]:checked');
      if (key && ticked) answers[key] = ticked.value;
    }
    return answers;
  }

  // Writes the profile attributes that the subscription endpoint won't.
  function upsertProfile(apiKey, attributes) {
    var profile = {};
    for (var key in attributes) {
      if (!Object.prototype.hasOwnProperty.call(attributes, key)) continue;
      // Consent belongs to the subscription call alone.
      if (key === 'subscriptions') continue;
      profile[key] = attributes[key];
    }

    return postProfile(apiKey, profile)
      .then(function (res) {
        if (res.ok || res.status === 202 || !profile.phone_number) return res;

        /*
          A rejected phone_number would otherwise take the name down with it.
          Retrying without the identifier still lands everything else — the
          number included, since it rides along as a property too.
        */
        var withoutPhone = {};
        for (var key in profile) {
          if (!Object.prototype.hasOwnProperty.call(profile, key)) continue;
          if (key === 'phone_number') continue;
          withoutPhone[key] = profile[key];
        }
        return postProfile(apiKey, withoutPhone);
      })
      .catch(function () {
        // The subscription is what the visitor sees — see submitToKlaviyo.
      });
  }

  function postProfile(apiKey, profile) {
    return fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(apiKey), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-02-15' },
      body: JSON.stringify({ data: { type: 'profile', attributes: profile } })
    });
  }

  function fieldValue(form, selector) {
    var input = form.querySelector(selector);
    return input ? input.value.trim() : '';
  }

  function resetFields(form) {
    var inputs = form.querySelectorAll('input');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].type === 'hidden' || inputs[i].type === 'submit') continue;
      // A checkbox's value is its answer; clearing it would lose the answer.
      if (inputs[i].type === 'checkbox' || inputs[i].type === 'radio') {
        inputs[i].checked = false;
        continue;
      }
      inputs[i].value = '';
    }
  }

  /* ----------------------------------------------------------------- messages */

  // Per-field message element, falling back to the form-level one the
  // single-field forms use.
  function validationElement(input) {
    var form = input.closest('.newsletter-form');
    if (!form) return null;
    var wrapper = input.closest('.field') || input.parentElement;
    return (
      (wrapper && wrapper.querySelector('[data-newsletter-validation]')) ||
      form.querySelector('[data-newsletter-validation]')
    );
  }

  function showValidationError(input, message) {
    var el = validationElement(input);
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    input.classList.add('invalid');
    input.setAttribute('aria-invalid', 'true');
  }

  function clearValidationError(input) {
    var el = validationElement(input);
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
    input.classList.remove('invalid');
    input.removeAttribute('aria-invalid');
  }

  function clearValidationErrors(form) {
    var inputs = form.querySelectorAll('input');
    for (var i = 0; i < inputs.length; i++) clearValidationError(inputs[i]);
    var groups = form.querySelectorAll('[data-klaviyo-required]');
    for (var g = 0; g < groups.length; g++) clearGroupError(groups[g]);
  }

  // A required question's message sits inside its own group, so it reads
  // under the answers rather than under the email field.
  function showGroupError(group, message) {
    var el = group.querySelector('[data-newsletter-validation]');
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    group.classList.add('invalid');
  }

  function clearGroupError(group) {
    var el = group.querySelector('[data-newsletter-validation]');
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
    group.classList.remove('invalid');
  }

  function showMessage(form, type) {
    var successEl = form.querySelector('[data-klaviyo-success]');
    var errorEl = form.querySelector('[data-klaviyo-error]');
    clearValidationErrors(form);

    if (type === 'success') {
      if (successEl) successEl.style.display = '';
      if (errorEl) errorEl.style.display = 'none';
      /*
        Let a container react to a completed signup. The promo popup listens
        for this to stop asking: its own dismissal cookie is only written when
        the close button is used, and a Klaviyo signup never reloads the page.
      */
      form.dispatchEvent(new CustomEvent('newsletter:subscribed', { bubbles: true }));
    } else {
      if (errorEl) errorEl.style.display = '';
      if (successEl) successEl.style.display = 'none';
    }
  }

  function hideStatusMessages(form) {
    var successEl = form.querySelector('[data-klaviyo-success]');
    var errorEl = form.querySelector('[data-klaviyo-error]');
    if (successEl) successEl.style.display = 'none';
    if (errorEl) errorEl.style.display = 'none';
    var nativeSuccess = form.querySelector('.newsletter-form__success:not([data-klaviyo-success])');
    if (nativeSuccess) nativeSuccess.style.display = 'none';
    var nativeError = form.querySelector('.form__message.errors:not([data-klaviyo-error])');
    if (nativeError) nativeError.style.display = 'none';
  }
})();
